"use client";

import React from "react";
import { ShieldCheck, Award, CircleCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
export default function StartYourClimateUpgrade() {
  return <section data-section-id="17089" id="start-your-recovery" className="relative w-full py-16 md:py-24 bg-primary dark overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full opacity-[0.05] bg-[radial-gradient(circle_at_center,_var(--accent)_1px,_transparent_1px)] [background-size:32px_32px]" />
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-secondary/20 rounded-full blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(auto,600px)_1fr] gap-8 items-start lg:items-center">
          
          {}
          <div className="hidden lg:flex flex-col items-end justify-center text-right space-y-4 animate-in fade-in slide-in-from-right-8 duration-700 delay-100">
            <div className="bg-card/10 backdrop-blur-md border border-white/10 p-6 rounded-xl shadow-2xl hover:shadow-accent/10 transition-shadow duration-300 max-w-[240px]">
              <div className="flex justify-end mb-3">
                <div className="p-3 bg-accent/20 rounded-full">
                  <ShieldCheck className="w-8 h-8 text-accent" />
                </div>
              </div>
              <h3 className="font-bold text-lg text-white mb-1">DPT&nbsp;</h3>
              <p className="text-sm text-slate-300">
                Doctor of Physical Therapy providing expert clinical care at your doorstep.
              </p>
            </div>
          </div>

          {}
          <div className="w-full z-10">
            <Card className="border-white/10 shadow-2xl bg-card/95 backdrop-blur-sm">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl md:text-3xl font-bold tracking-tight text-primary">
                  Start Your Recovery
                </CardTitle>
                <CardDescription className="text-base mt-2 text-muted-foreground">
                  Book your mobile concierge evaluation in Monmouth County.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 md:p-8">
                <FormProvider formKey="start_recovery_form_home" formId="start_recovery_form_home" sectionName="start-your-recovery" defaultValues={{
                name: "",
                email: "",
                phone: "",
                goal: "",
                focus_areas: [],
                location: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                goal: {
                  required: true
                },
                focus_areas: {
                  required: true
                },
                location: {
                  required: true
                }
              }} submitText="Book Your Evaluation" successMessage="Request received! Dr. Dave will contact you shortly to confirm your session." errorMessage="Something went wrong. Please try again or call us directly.">
                  <UpgradeForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="hidden lg:flex flex-col items-start justify-center text-left space-y-4 animate-in fade-in slide-in-from-left-8 duration-700 delay-100">
            <div className="bg-card/10 backdrop-blur-md border border-white/10 p-6 rounded-xl shadow-2xl hover:shadow-accent/10 transition-shadow duration-300 max-w-[240px]">
              <div className="flex justify-start mb-3">
                <div className="p-3 bg-secondary/20 rounded-full !bg-[url(https://ruling-egret.10web.cloud/wp-content/uploads/2026/04/tpi-certified-medical-level-2-dark-md.jpg)]">
                  <Award className="w-8 h-8 text-secondary-foreground" />
                </div>
              </div>
              <h3 className="font-bold text-lg text-white mb-1">TPI Certified</h3>
              <p className="text-sm text-slate-300">
                Titleist Performance Institute certified to optimize your athletic performance.
              </p>
            </div>
          </div>

          {}
          <div className="lg:hidden col-span-1 flex flex-col sm:flex-row gap-4 justify-center items-center mt-4">
            <div className="flex items-center gap-3 bg-card/10 border border-white/10 px-4 py-3 rounded-lg shadow-sm">
              <ShieldCheck className="w-5 h-5 text-accent" />
              <span className="font-semibold text-sm text-white">DPT Certified</span>
            </div>
            <div className="flex items-center gap-3 bg-card/10 border border-white/10 px-4 py-3 rounded-lg shadow-sm">
              <Award className="w-5 h-5 text-secondary-foreground" />
              <span className="font-semibold text-sm text-white">TPI Certified</span>
            </div>
          </div>

        </div>
      </div>
    </section>;
}
function UpgradeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-emerald-500/20 text-emerald-500 rounded-full flex items-center justify-center mb-2">
          <CircleCheck className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Booking Request Sent</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button variant="outline" className="mt-4 border-primary text-primary hover:bg-primary/5" onClick={() => window.location.reload()}>
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      {}
      <FormField control={control} name="goal" render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="text-base font-bold text-primary">Primary Goal</FormLabel>
            <FormControl>
              <ToggleGroup type="single" value={field.value} onValueChange={field.onChange} className="justify-start w-full grid grid-cols-2 gap-2">
                <ToggleGroupItem value="Injury Rehab" className="h-12 border border-input data-[state=on]:bg-primary data-[state=on]:text-primary-foreground hover:bg-muted font-medium transition-all">
                  Injury Rehab
                </ToggleGroupItem>
                <ToggleGroupItem value="Performance" className="h-12 border border-input data-[state=on]:bg-primary data-[state=on]:text-primary-foreground hover:bg-muted font-medium transition-all">
                  Performance
                </ToggleGroupItem>
              </ToggleGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Dave Garaffa" {...field} className="bg-background border-input focus:ring-accent" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="860-716-9870" {...field} className="bg-background border-input focus:ring-accent" />
              </FormControl>
              <FormDescription className="text-xs">
                For scheduling your mobile concierge visit.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="davidgaraffa@gmail.com" {...field} className="bg-background border-input focus:ring-accent" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="location" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold">Town in Monmouth County</FormLabel>
              <FormControl>
                <Input placeholder="Rumson, NJ" {...field} className="bg-background border-input focus:ring-accent" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="focus_areas" render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="text-base font-bold text-primary">I want to...</FormLabel>
            <FormControl>
              <ToggleGroup type="multiple" value={field.value || []} onValueChange={field.onChange} className="flex flex-wrap justify-start gap-2">
                {["Return to Sport", "Lift without Pain", "Keep up with Family", "Post-Op Recovery", "Golf Screen"].map(service => <ToggleGroupItem key={service} value={service} className="border border-input px-3 py-2 h-auto text-sm font-medium data-[state=on]:bg-secondary data-[state=on]:text-secondary-foreground data-[state=on]:border-secondary hover:bg-muted transition-all rounded-md">
                    {service}
                  </ToggleGroupItem>)}
              </ToggleGroup>
            </FormControl>
            <FormDescription className="text-xs">Select all that apply to your recovery journey.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive font-medium">
          {submitError}
        </div>}

      <Button type="submit" size="lg" className="w-full text-base font-bold h-12 shadow-lg bg-accent text-accent-foreground hover:bg-accent/90 transition-all" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Scheduling Evaluation...
          </> : "Book Your Evaluation"}
      </Button>
    </Form>;
}